import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Components/UI/select";
import { Label } from "../Components/UI/label";
import { X } from "lucide-react";
import {
  IncidentQueryField,
  GroupByCriteria,
  GROUPABLE_FIELD_TYPES,
  FieldType,
} from "../types/salesforce";

interface GroupBySelectorProps {
  className?: string;
  selectedFields: any[];
  availableFields: IncidentQueryField[];
  groupBy: GroupByCriteria[];
  onGroupByChange: (groupBy: GroupByCriteria[]) => void;
}

export const GroupBySelector: React.FC<GroupBySelectorProps> = ({
  className,
  selectedFields,
  availableFields,
  groupBy,
  onGroupByChange,
}) => {
  // Filter available fields to only show those that are selected and groupable
  const availableGroupFields = availableFields.filter(
    (field) =>
      selectedFields.includes(field.Field_API_Name__c) &&
      GROUPABLE_FIELD_TYPES.includes(field.Field_Type__c as FieldType)
  );

  const handleAddGrouping = (fieldName: string) => {
    if (!fieldName) return;

    const selectedField = availableFields.find(
      (f) => f.Field_API_Name__c === fieldName
    );

    if (!selectedField) return;

    const isDateField =
      selectedField.Field_Type__c === "Date" ||
      selectedField.Field_Type__c === "DateTime";

    if (isDateField) {
      onGroupByChange([
        ...groupBy,
        {
          fieldName,
          level: 1,
          dateGranularity: "month",
        },
      ]);
    } else {
      onGroupByChange([
        ...groupBy,
        {
          fieldName,
          level: 1,
        },
      ]);
    }
  };

  const handleRemoveGrouping = (index: number) => {
    const newGroupBy = [...groupBy];
    newGroupBy.splice(index, 1);
    onGroupByChange(newGroupBy);
  };

  const handleUpdateGranularity = (
    index: number,
    dateGranularity: "day" | "week" | "month" | "quarter" | "year"
  ) => {
    const newGroupBy = [...groupBy];
    newGroupBy[index] = {
      ...newGroupBy[index],
      dateGranularity,
    };
    onGroupByChange(newGroupBy);
  };

  const getFieldLabel = (fieldName: string) => {
    const field = availableFields.find(
      (f) => f.Field_API_Name__c === fieldName
    );
    return field ? `${field.Label} (${field.Field_Type__c})` : fieldName;
  };

  return (
    <div className={`space-y-2 rounded bg-white-main p-4 ${className}`}>
      <div className="flex items-center gap-2">
        <Label className="text-gray-600">Field Group</Label>
      </div>

      <div className="flex flex-col space-y-2">
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          {/* Select field container */}
          <div className="w-full sm:w-[180px]">
            <Select
              value=""
              onValueChange={(newValue) => {
                if (groupBy.length < 2) handleAddGrouping(newValue);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {availableGroupFields.map((field) => (
                  <SelectItem
                    key={field.Field_API_Name__c}
                    value={field.Field_API_Name__c}
                    disabled={groupBy.some(
                      (g) =>
                        g.fieldName === field.Field_API_Name__c ||
                        groupBy.length >= 2
                    )}
                  >
                    {field.Label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected fields container */}
          {groupBy.length > 0 && (
            <div className="flex-1 space-y-2">
              {groupBy.map((grouping, index) => {
                const field = availableFields.find(
                  (f) => f.Field_API_Name__c === grouping.fieldName
                );
                if (!field) return null;

                const isDateField =
                  field.Field_Type__c === "Date" ||
                  field.Field_Type__c === "DateTime";

                return (
                  <div
                    key={grouping.fieldName}
                    className="flex items-center gap-2 rounded bg-white-main"
                  >
                    <span className="flex-1 text-[0.7rem]">{field.Label}</span>
                    {isDateField && grouping.dateGranularity && (
                      <Select
                        value={grouping.dateGranularity}
                        onValueChange={(value) =>
                          handleUpdateGranularity(
                            index,
                            value as
                              | "day"
                              | "week"
                              | "month"
                              | "quarter"
                              | "year"
                          )
                        }
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="day">Day</SelectItem>
                          <SelectItem value="week">Week</SelectItem>
                          <SelectItem value="month">Month</SelectItem>
                          <SelectItem value="quarter">Quarter</SelectItem>
                          <SelectItem value="year">Year</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <button
                      onClick={() => handleRemoveGrouping(index)}
                      className="hover:text-accent-foreground inline-flex cursor-pointer rounded-md border-0 text-xs font-medium text-red-500 hover:bg-red-100 disabled:pointer-events-none disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {groupBy.length > 0 && (
          <div className="pl-2 text-xs text-gray-500">
            Currently grouping by:{" "}
            {groupBy
              .map((g) => {
                const fieldLabel = getFieldLabel(g.fieldName);
                return g.dateGranularity
                  ? `${fieldLabel} (by ${g.dateGranularity})`
                  : fieldLabel;
              })
              .join(" → ")}
          </div>
        )}
      </div>
    </div>
  );
};
